import {
  isSameHexColor,
  normalizeHex,
  resolveThemeColorTargets,
} from "./customPrimaryColor";

type ThemeColorMode = "light" | "dark";

/** 与 theme.css 默认 --btn-default-text（= --text-primary）一致 */
const DEFAULT_BTN_DEPUTY_TEXT_BY_MODE: Record<ThemeColorMode, string> = {
  light: "#1E2023",
  dark: "#EEEEEE",
};

const CUSTOM_BTN_DEPUTY_TEXT_STORAGE_KEY = "app-custom-btn-deputy-text-colors";

export type CustomBtnDeputyTextColors = Partial<Record<ThemeColorMode, string>>;

export const getDefaultBtnDeputyTextByMode = (mode: ThemeColorMode): string =>
  DEFAULT_BTN_DEPUTY_TEXT_BY_MODE[mode];

export const isDefaultBtnDeputyTextColor = (
  mode: ThemeColorMode,
  color: string
) => isSameHexColor(color, getDefaultBtnDeputyTextByMode(mode));

const parseStored = (raw: string | null): CustomBtnDeputyTextColors => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as CustomBtnDeputyTextColors;
    return {
      light: parsed.light ? normalizeHex(parsed.light) || undefined : undefined,
      dark: parsed.dark ? normalizeHex(parsed.dark) || undefined : undefined,
    };
  } catch {
    return {};
  }
};

export const readStoredCustomBtnDeputyTextColors =
  (): CustomBtnDeputyTextColors =>
    parseStored(localStorage.getItem(CUSTOM_BTN_DEPUTY_TEXT_STORAGE_KEY));

export const writeStoredCustomBtnDeputyTextColor = (
  mode: ThemeColorMode,
  color: string | null
) => {
  const next = readStoredCustomBtnDeputyTextColors();
  if (color) {
    next[mode] = color;
  } else {
    delete next[mode];
  }
  localStorage.setItem(
    CUSTOM_BTN_DEPUTY_TEXT_STORAGE_KEY,
    JSON.stringify(next)
  );
};

const writeBtnDeputyTextVar = (
  target: HTMLElement,
  mode: ThemeColorMode,
  color: string
) => {
  target.style.setProperty(`--custom-btn-deputy-text-${mode}`, color);
};

export const clearCustomBtnDeputyTextCssVars = (
  mode: ThemeColorMode,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    target.style.removeProperty(`--custom-btn-deputy-text-${mode}`);
  });
};

export const applyBtnDeputyTextColor = (
  mode: ThemeColorMode,
  color: string,
  element?: HTMLElement
) => {
  resolveThemeColorTargets(element).forEach((target) => {
    writeBtnDeputyTextVar(target, mode, color);
  });
};

export const readAppliedBtnDeputyTextColor = (mode: ThemeColorMode) => {
  const stored = readStoredCustomBtnDeputyTextColors()[mode];
  if (stored) return stored;
  return getDefaultBtnDeputyTextByMode(mode);
};

export const restoreStoredCustomBtnDeputyTextColors = (
  element?: HTMLElement
) => {
  const stored = readStoredCustomBtnDeputyTextColors();
  resolveThemeColorTargets(element).forEach((target) => {
    (["light", "dark"] as const).forEach((mode) => {
      const color = stored[mode];
      if (color) writeBtnDeputyTextVar(target, mode, color);
    });
  });
};
